import { unformat } from "@react-input/mask";

import { buildMaskConfig } from "utils/mask";

import type { ConfidentialityLevel } from "./interface";
import type { IServerLinkOption } from "./ServerLinkDrawerContent";

type AudienceConfidentialityFields = {
  isClassified?: boolean | number | null;
  IsClassified?: boolean | number | null;
  confidentialityLevel?: boolean | number | null;
  ConfidentialityLevel?: boolean | number | null;
};

export const getAudienceConfidentialityLevel = (
  audience: AudienceConfidentialityFields | null | undefined,
  isCartorioProduct: boolean,
) => {
  if (isCartorioProduct) {
    return audience?.isClassified ?? audience?.IsClassified;
  }

  return (
    audience?.confidentialityLevel ??
    audience?.ConfidentialityLevel ??
    audience?.isClassified ??
    audience?.IsClassified
  );
};

export const mapUserToServerLinkOption = (item: any): IServerLinkOption => {
  const userData = item?.userCustomer ?? item?.user ?? item;
  const isConfidentialityLink =
    item?.hearingId !== undefined && item?.userCustomerId !== undefined;
  const email =
    item?.email ??
    item?.userCustomerEmail ??
    userData?.email ??
    userData?.userCustomerEmail;
  const cpf =
    item?.cpf ?? item?.document ?? userData?.cpf ?? userData?.document;
  const name =
    item?.name ??
    item?.userCustomerName ??
    userData?.name ??
    userData?.userCustomerName;
  const rawUserCustomerId =
    item?.userCustomerId ?? userData?.userCustomerId ?? userData?.id;
  const userCustomerId =
    rawUserCustomerId !== null && rawUserCustomerId !== undefined
      ? Number(rawUserCustomerId)
      : undefined;
  const confidentialityId =
    isConfidentialityLink && item?.id !== null && item?.id !== undefined
      ? Number(item.id)
      : undefined;
  const normalizedEmail = email ? String(email) : "";
  const normalizedCpf = cpf ? String(cpf) : null;
  const normalizedName = name ? String(name) : "";
  const id =
    userCustomerId && Number.isFinite(userCustomerId)
      ? String(userCustomerId)
      : normalizedEmail || normalizedCpf || normalizedName;
  const labelDetails = [
    normalizedName,
    normalizedEmail,
    normalizedCpf ? `CPF: ${normalizedCpf}` : "",
  ]
    .filter(Boolean)
    .join(" - ");
  const fallbackLabel = userCustomerId
    ? `Usu\u00e1rio ${userCustomerId}`
    : normalizedName;

  return {
    id,
    value: id,
    label: labelDetails || fallbackLabel,
    userCustomerId:
      userCustomerId && Number.isFinite(userCustomerId)
        ? userCustomerId
        : undefined,
    confidentialityId:
      confidentialityId && Number.isFinite(confidentialityId)
        ? confidentialityId
        : undefined,
    name: normalizedName,
    email: normalizedEmail,
    cpf: normalizedCpf,
  };
};

export const normalizeServerLinkCpf = (value?: string | null) =>
  String(value ?? "").replace(/\D/g, "");

export const normalizeServerLinkEmail = (value?: string) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

export const normalizeServerLinkSearchTerm = (value: string) => {
  const trimmedValue = value.trim();
  const onlyDigits = trimmedValue.replace(/\D/g, "");
  const hasCpfLength = onlyDigits.length === 11;
  const hasCpfMaskChars = /[.-]/.test(trimmedValue);

  if (hasCpfLength && (hasCpfMaskChars || !trimmedValue.includes("@"))) {
    return onlyDigits;
  }

  return trimmedValue;
};

export const isValidProcedureNumber = (value: string, mask?: string) => {
  if (!mask) {
    return true;
  }

  const maskConfig = buildMaskConfig(mask);
  if (!maskConfig.replacement || !maskConfig.expectedLength) {
    return true;
  }

  const unmaskedLength = unformat(value ?? "", {
    mask: maskConfig.mask,
    replacement: maskConfig.replacement,
  }).length;

  return unmaskedLength === maskConfig.expectedLength;
};

export const normalizeConfidentialityLevel = (
  value: unknown,
): ConfidentialityLevel => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return value ? 1 : 0;
  }

  if (numericValue <= 0) return 0;
  if (numericValue >= 5) return 5;

  return Math.trunc(numericValue) as ConfidentialityLevel;
};
